import React, { useState } from 'react';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { withLocalePrefix } from 'shared/utils/translation';
import { standardRequestWithErrorFlash } from 'shared/utils/request';
import Form from '@grnhse/seedling/lib/birch/components/forms/form';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import { SecondaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import SkeletonLoader from '@grnhse/seedling/lib/birch/components/skeleton_loader';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { useSendEmailData } from 'talent_matching/hooks/use_send_email_data';
import EmailSection from 'talent_matching/components/bulk_actions/send_email/email_section';
import ModalFooter from 'talent_matching/components/bulk_actions/send_email/modal_footer';
import { useQueryClient } from '@tanstack/react-query';
import { useRediscoveryTabContext } from 'talent_matching/contexts/rediscovery_tab_context';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { createBulkRediscoveryActions } from 'talent_matching/api';
import { useTrackBulkAction } from 'talent_matching/lib/use_track_bulk_action';
import type { RediscoveryApplication } from 'talent_matching/types';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';

const t = withLocalePrefix('talent_matching.bulk_actions.send_email');

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  applicationIds: number[];
  totalSelected: number;
};

type SendEmailForm = {
  template: string;
  from: Item;
  cc: Item[];
  cc_recruiter: boolean;
  cc_coordinator: boolean;
  include_self: boolean;
  subject: string;
  html_body: string;
};

const BulkSendEmailModal = ({
  isOpen,
  closeModal,
  applicationIds,
  totalSelected,
}: Props) => {
  const {
    paths: { emailModalPath },
  } = useRediscoveryTabContext();
  const { hiringPlanId, userId, currentUserName } = useUserContext();
  const trackBulk = useTrackBulkAction('past_candidates');
  const queryClient = useQueryClient();

  const [submitting, setSubmitting] = useState(false);

  const { data, error, refetch, isFetching, isLoading } = useSendEmailData(
    emailModalPath ?? ''
  );

  const loading = isFetching || isLoading;

  if (!emailModalPath || loading || error || !data) {
    return (
      <DataEntryModal
        isOpen={isOpen}
        onDismiss={closeModal}
        ariaLabel={t('title')}
        size="large"
      >
        <DataEntryModal.Header onClick={closeModal}>
          {t('title', { count: totalSelected })}
        </DataEntryModal.Header>

        <DataEntryModal.Body>
          {loading && <SkeletonLoader columnsPerRow={1} rows={5} />}

          {!loading && (error || !data) && (
            <Stack
              spacing={Tokens.spacing['2']}
              direction="row"
              alignItems="center"
            >
              <Body>{t('error_loading')}</Body>
              <SecondaryButton onClick={() => refetch()} size="md">
                {t('retry')}
              </SecondaryButton>
            </Stack>
          )}
        </DataEntryModal.Body>
      </DataEntryModal>
    );
  }

  const onSubmit = async (formData: SendEmailForm) => {
    setSubmitting(true);
    const payload = {
      email_bulk_candidate: {
        from: formData.from.value,
        subject: formData.subject,
        html_body: formData.html_body,
        cc: formData.cc.map((c) => c.value).join(','),
        cc_recruiter: formData.cc_recruiter && '1',
        cc_coordinator: formData.cc_coordinator && '1',
        include_self: formData.include_self && '1',
      },
      application_ids: applicationIds,
    };

    try {
      const res = await standardRequestWithErrorFlash.post(
        data.bulkEmailCandidatesPath,
        payload,
        {
          successMessage: t('success', { count: totalSelected }),
        }
      );
      if (res.data.status === 'success') {
        trackBulk('send_email', applicationIds);
        createBulkRediscoveryActions({
          action_type: 'sent_email',
          application_ids: applicationIds,
          hiring_plan_id: hiringPlanId,
        });
        const now = new Date().toISOString();
        applicationIds.forEach((id, index) => {
          const cached = queryClient.getQueryData<RediscoveryApplication>([
            'rediscovery-application',
            id,
          ]);
          if (!cached) {
            return;
          }
          queryClient.setQueryData(['rediscovery-application', id], {
            ...cached,
            rediscoveryActions: [
              {
                id: -(index + 1),
                action: 'sent_email',
                metadata: null,
                organization_id: 0,
                hiring_plan_id: hiringPlanId,
                application_id: id,
                user_id: userId,
                user_name: currentUserName,
                created_at: now,
                updated_at: now,
              },
              ...cached.rediscoveryActions,
            ],
          });
        });
        closeModal();
      } else {
        setSubmitting(false);
      }
    } catch {
      setSubmitting(false);
    }
  };

  const defaultValues = {
    template: data.defaultTemplate,
    from: '',
    cc: [],
    cc_recruiter: false,
    cc_coordinator: false,
    include_self: false,
    subject: '',
    html_body: '',
  };

  return (
    <DataEntryModal
      isOpen={isOpen}
      onDismiss={closeModal}
      ariaLabel={t('title')}
      size="large"
    >
      <Form onSubmit={onSubmit} defaultValues={defaultValues} mode="onSubmit">
        <DataEntryModal.Header onClick={closeModal}>
          {t('title', { count: totalSelected })}
        </DataEntryModal.Header>
        <DataEntryModal.Body>
          <EmailSection
            totalSelected={totalSelected}
            templateOptions={data.templateOptions}
            fromOptions={data.fromOptions}
            getCcOptionsPath={data.getCcOptionsPath}
            displayDomainWarning={data.displayDomainWarning}
            tokens={data.tokens}
          />
        </DataEntryModal.Body>
        <ModalFooter closeModal={closeModal} disableButtons={submitting} />
      </Form>
    </DataEntryModal>
  );
};

export default BulkSendEmailModal;
